// packages import
import { createContext, FC, Reducer, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'

// others
import { Bill, useFetchFundRequestsLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { ChildrenType } from '../interfaceTypes'

import { AuthContext } from './AuthContext'
import { AppContext } from './AppContext'
import { ActionType } from './AppContextReducer'
import {
  FundRequestsAction,
  FundRequestsActionType,
  FundRequestsFilters,
  fundRequestsInitialState,
  fundRequestsReducer,
  FundRequestsState
} from '../components/billing/fundRequests/FundRequestsReducer'
import { useSearchParams } from 'react-router-dom'

const PAGE_LIMIT = 20
export interface FundRequestsContextType extends Omit<FundRequestsState, 'currentPage' | 'totalPages' | 'totalCount'> {
  dispatch: React.Dispatch<FundRequestsAction>
  refetch: () => void
  setFilters: (filters: FundRequestsFilters) => void
}

export const FundRequestsContext = createContext<FundRequestsContextType>({
  ...fundRequestsInitialState,
  dispatch: () => {
    return
  },
  refetch: () => {
    return
  },
  setFilters: () => {
    return
  }
})

export const FundRequestsContextProvider: FC<ChildrenType> = ({ children }): JSX.Element => {
  const { isLoggedIn } = useContext(AuthContext)
  const { dispatch: appDispatch } = useContext(AppContext)
  const [state, dispatch] = useReducer<Reducer<FundRequestsState, FundRequestsAction>>(
    fundRequestsReducer,
    fundRequestsInitialState
  )
  const [searchParams, setSearchParams] = useSearchParams()

  const currentPage = useMemo(() => parseInt(searchParams.get('currentPage') || '1'), [searchParams])

  const [fetchFundRequestsQuery, { loading, refetch }] = useFetchFundRequestsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {
      dispatch({ type: FundRequestsActionType.SET_FUND_REQUESTS, fundRequests: [] })
      dispatch({ type: FundRequestsActionType.SET_LOADING, loading: false })
    },
    onCompleted(data) {
      if (data?.fetchFundRequests) {
        const { fundRequests, pagination } = data.fetchFundRequests

        if (fundRequests) {
          dispatch({ type: FundRequestsActionType.SET_FUND_REQUESTS, fundRequests: fundRequests as Bill[] })
        }

        if (pagination) {
          const { totalPages, totalCount } = pagination
          totalPages && appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
          totalCount && appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
        }

        dispatch({ type: FundRequestsActionType.SET_LOADING, loading: false })
      }
    }
  })

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: FundRequestsActionType.SET_LOADING, loading: true })

      fetchFundRequestsQuery({
        variables: {
          fetchFundRequestsInput: {
            searchTerm: state.filters.searchTerm,
            page: currentPage,
            limit: PAGE_LIMIT,
            status: state.filters.status,
            matterId: state.filters.matter?.id
          }
        }
      })
    }
  }, [state.filters, currentPage, isLoggedIn, appDispatch, fetchFundRequestsQuery])

  // Sync matterId from URL into filters when user lands with ?matterId=xxx
  useEffect(() => {
    const matterIdFromUrl = searchParams.get('matterId')
    if (matterIdFromUrl && state.filters.matter?.id !== matterIdFromUrl) {
      dispatch({
        type: FundRequestsActionType.SET_FILTERS,
        filters: { matter: { id: matterIdFromUrl } }
      })
    }
  }, [searchParams, state.filters.matter?.id])

  useEffect(() => {
    // Set current page from query params
    appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage })

    return () => {
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [])

  const setCurrentPage = useCallback(
    (page: number) => {
      searchParams.set('currentPage', page.toString())
      setSearchParams(searchParams)

      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: page })
    },
    [appDispatch, searchParams, setSearchParams]
  )

  const setFilters = useCallback(
    (filters: FundRequestsFilters) => {
      dispatch({ type: FundRequestsActionType.SET_FILTERS, filters })
      const next = new URLSearchParams(searchParams)
      next.set('currentPage', '1')
      if (Object.prototype.hasOwnProperty.call(filters, 'matter')) {
        if (filters.matter) {
          next.set('matterId', filters.matter.id)
        } else {
          next.delete('matterId')
        }
      }
      setSearchParams(next)
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    },
    [searchParams, setSearchParams, appDispatch]
  )

  const contextValue = useMemo(
    () => ({
      ...state,
      loading: loading || state.loading,
      dispatch,
      refetch,
      setFilters
    }),
    [state, loading, refetch, setFilters]
  )

  return <FundRequestsContext.Provider value={contextValue}>{children}</FundRequestsContext.Provider>
}
